import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { GET_GALLERY_PHOTO_BY_SLUG } from "../queries";
import "./gallery-card.css";
import "./single-gallery.css";
import { useQuery } from "@apollo/client";
import Skeleton from "react-loading-skeleton";

interface Photo {
  id: string;
  title: string;
  sourceUrl: string;
  mediaDetails: {
    sizes: [{ name: string; sourceUrl: string }];
  };
}

interface Author {
  id: string;
  name: string;
  slug: string;
  description: string;
  avatar: {
    url: string;
  };
}

export default function SingleGallery() {
  const [photoIndex, setPhotoIndex] = useState<number>(0);
  const [photoSource, setPhotoSource] = useState<string | undefined>();
  const [photoArray, setPhotoArray] = useState<Photo[] | undefined>();
  const [author, setAuthor] = useState<Author | null>(null);

  const { gallerySlug } = useParams();

  const {
    loading,
    data: photoData,
    error,
  } = useQuery(GET_GALLERY_PHOTO_BY_SLUG, { variables: { slug: gallerySlug } });

  useEffect(() => {
    setPhotoArray(photoData?.gallery?.galleryFields?.galleryPhotos?.nodes);
  }, [photoData?.gallery?.galleryFields?.galleryPhotos?.nodes]);

  useEffect(() => {
    if (photoArray) {
      setPhotoSource(photoArray.at(photoIndex)?.sourceUrl);
    }
  }, [photoIndex, photoArray]);

  useEffect(() => {
    if (photoData?.gallery?.author?.node) {
      setAuthor(photoData.gallery.author.node);
    }
  }, [photoData]);

  useEffect(() => {
    console.log(photoData);

    const nextIndex = photoIndex + 1;

    if (photoArray?.at(nextIndex)?.sourceUrl) {
      const img = new Image();

      img.src = photoArray[nextIndex].sourceUrl;
    }
  });

  function nextPhoto() {
    if (photoArray?.at(photoIndex + 1) !== undefined) {
      console.log("not undefined");
      setPhotoIndex(photoIndex + 1);
    } else setPhotoIndex(0);
  }

  function prevPhoto() {
    if (photoIndex > 0) {
      setPhotoIndex(photoIndex - 1);
    } else if (photoArray !== undefined && photoArray?.length > 0) {
      setPhotoIndex(photoArray?.length - 1);
    }
  }

  if (error) {
    return <h1>Error loading: {error.message}</h1>;
  }

  if (loading)
    return (
      <div className="max-w-8">
        <h1 className="text-center">
          <Skeleton />
        </h1>
        <div className="carousel-container">
          <button type="button" className="carousel-button" onClick={prevPhoto}>
            <svg
              height="20px"
              width="20px"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
          </button>

          <div className="carousel_image">
            <Skeleton width="100%" height="600px" />
          </div>
          <button
            type="button"
            className="carousel-button"
            id="next-button"
            onClick={nextPhoto}
          >
            <svg
              height="20px"
              width="20px"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
        </div>

        <hr />
        <div className="row mt-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div className="col-sm-2 mb-2" key={i}>
              <Skeleton height="150px" />
            </div>
          ))}
        </div>
      </div>
    );

  return (
    <div className="container mx-auto">
      <h1 className="text-center text-5xl font-heading font-semibold my-6">
        {photoData.gallery?.title}
      </h1>
      <div className="carousel-container">
        <button type="button" className="carousel-button" onClick={prevPhoto}>
          <svg
            height="20px"
            width="20px"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            ></path>
          </svg>
        </button>

        <img src={photoSource} alt="gallery" className="carousel-image" />
        <button
          type="button"
          className="carousel-button"
          id="next-button"
          onClick={nextPhoto}
        >
          <svg
            height="20px"
            width="20px"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
        </button>
      </div>
      <hr />
      <div className="flex flex-wrap gap-0">
        {photoArray &&
          photoArray.map((photo, index) => {
            console.log(photo);
            return (
              <div className="col-sm-2 m-1 md:m-2" key={photo.id}>
                <div
                  className={
                    index === photoIndex
                      ? "border-solid border-4 rounded  border-green-600"
                      : ""
                  }
                >
                  <button
                    className="m-0 p-0 block w-full"
                    onClick={() => setPhotoIndex(index)}
                  >
                    <img
                      className="block"
                      src={
                        photo.mediaDetails.sizes.find(
                          (s) => s.name === "thumbnail",
                        )?.sourceUrl
                      }
                      alt="gallery item"
                    />
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {author && (
        <div className="container mt-5">
          <div className="d-flex align-items-center p-4 bg-light rounded border">
            {/* Avatar Section */}
            {author.avatar?.url && (
              <div className="flex-shrink-0 me-4">
                <img
                  src={author.avatar.url}
                  alt={author.name}
                  className="rounded-circle border"
                  width="80"
                  height="80"
                  style={{ objectFit: "cover" }}
                />
              </div>
            )}

            {/* Text Section */}
            <div className="flex-grow-1">
              <h4 className="mb-1">{author.name}</h4>
              {author.description ? (
                <p className="text-muted mb-0">{author.description}</p>
              ) : (
                <p className="text-muted mb-0 fst-italic">Author</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
